import React, { createContext, useContext, useState, useCallback } from 'react';
import { AuthApiError } from '@supabase/supabase-js';
import { useSupabaseClient } from '../lib/supabaseClient';
import { useAuthContext } from './AuthContext';
import { handleAuthFailure } from '../utils/handleAuthFailure';
import { sanitizeNumericFields } from '../utils/sanitizeNumericFields';
import logDev from '../utils/logDev';
import getIsoTimestamp from '../utils/getIsoTimestamp';

// Basic UUID v4 format validation helper
const isValidUuid = (id) =>
  typeof id === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

// Columns in financial_analyses_pf that do not allow null values
const DEFAULT_ANALYSIS_VALUES = {
  income_sources: [],
  expenses: [],
  assets: [],
  liabilities: [],
  insurance_policies: [],
  financial_goals: [],
  estate_checklist: {},
  insurance_calculator: {},
  legacy_wishes: '',
  client_email: ''
};

// Ensure all non-nullable columns exist in the payload
const applyDefaultAnalysisValues = (obj = {}) => {
  const result = { ...obj };
  Object.entries(DEFAULT_ANALYSIS_VALUES).forEach(([key, val]) => {
    if (result[key] == null) {
      result[key] = Array.isArray(val)
        ? []
        : typeof val === 'object'
        ? { ...val }
        : val;
    }
  });
  return result;
};

const FinancialAnalysisContext = createContext();

export const useFinancialAnalysis = () => {
  const context = useContext(FinancialAnalysisContext);
  if (!context) {
    throw new Error('useFinancialAnalysis must be used within a FinancialAnalysisProvider');
  }
  return context;
};

// Export the provider component directly
export const FinancialAnalysisProvider = ({ children }) => {
  const { user, signOut } = useAuthContext();
  const supabase = useSupabaseClient();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  logDev('FinancialAnalysisProvider rendering, user:', user?.id);

  // Load analysis data for a specific client
  // If createIfMissing is false, do not insert a new record when none exists
  const loadAnalysis = useCallback(async (clientId, createIfMissing = true) => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('financial_analyses_pf')
        .select('*')
        .eq('client_id', clientId);

      if (user.role === 'advisor') {
        query = query.eq('created_by', user.id);
      } else if (user.role === 'admin') {
        // Admins may load analyses for any client without restricting by creator.
      }

      const { data, error: fetchError } = await query
        // Order newest first and limit to a single row to avoid PGRST116 when duplicates exist
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (data) {
        setAnalysis(data);
      } else if (createIfMissing) {
        if (!isValidUuid(clientId) || !isValidUuid(user?.id)) {
          setError('Invalid client or user identifier. Unable to create financial analysis.');
          return;
        }

        const newAnalysis = {
          client_id: clientId,
          created_by: user.id,
          created_at: getIsoTimestamp(),
          updated_at: getIsoTimestamp(),
          income_sources: [],
          expenses: [],
          assets: [],
          liabilities: [],
          insurance_policies: [],
          financial_goals: [],
          estate_checklist: {
            will: { completed: false, lastUpdated: '', notes: '' },
            powerOfAttorney: { completed: false, lastUpdated: '', notes: '' },
            healthcareDirective: { completed: false, lastUpdated: '', notes: '' },
            trust: { completed: false, lastUpdated: '', notes: '' },
            beneficiaryDesignations: { completed: false, lastUpdated: '', notes: '' },
            guardianship: { completed: false, lastUpdated: '', notes: '' },
            emergencyFund: { completed: false, lastUpdated: '', notes: '' },
            taxPlanning: { completed: false, lastUpdated: '', notes: '' }
          },
          legacy_wishes: ''
        };

        const { data: inserted, error: insertError } = await supabase
          .from('financial_analyses_pf')
          .insert(newAnalysis)
          .select()
          .maybeSingle();

        if (insertError) {
          console.error('Insert error details:', insertError);
          let message;
          if (insertError.code === '23503') {
            message = 'foreign key violation';
          } else if (insertError.code === '23502') {
            message = 'missing required field';
          } else if (insertError.status === 400) {
            message = 'Unable to create financial analysis. Please check the provided data.';
          } else {
            message = 'Unable to create financial analysis.';
          }
          setError(message);
          return { success: false, error: message };
        }
        setAnalysis(inserted || { ...newAnalysis });
      } else {
        setAnalysis(null);
        setError('No financial analysis found for this client.');
      }
    } catch (err) {
      const isAuthError = err instanceof AuthApiError || err?.status === 401;
      const handled = isAuthError ? await handleAuthFailure(err, signOut) : false;
      if (!handled) {
        console.error('Error loading analysis:', err);
        let message;
        if (err.code === '23503') {
          message = 'foreign key violation';
        } else if (err.code === '23502') {
          message = 'missing required field';
        } else if (err?.status === 400) {
          message = 'Invalid analysis data supplied.';
        } else {
          message = err.message || 'Failed to load financial analysis data.';
        }
        setError((prev) => prev || message);
        return { success: false, error: message };
      }
    } finally {
      setLoading(false);
    }
  }, [user, signOut]);

  // Save or update analysis data
  const saveAnalysis = useCallback(async (data) => {
    let payload;
    try {
      setSaving(true);
      setError(null);

      const withDefaults = applyDefaultAnalysisValues(data);
      payload = {
        ...withDefaults,
        updated_at: getIsoTimestamp()
      };

      if (user.role !== 'client' && !payload.created_by) {
        payload.created_by = user.id;
      }

      let saved;
      if (payload.id) {
        let updateQuery = supabase
          .from('financial_analyses_pf')
          .update(payload)
          .eq('id', payload.id)
          .eq('client_id', payload.client_id);

        if (user.role === 'client') {
          updateQuery = updateQuery.eq('client_id', user.id);
        } else if (user.role === 'advisor') {
          updateQuery = updateQuery.eq('created_by', user.id);
        } else if (user.role === 'admin') {
          // Admins bypass advisor-only ownership constraints when updating.
        }

        const { data: updated, error } = await updateQuery
          .select()
          .maybeSingle();

        if (error) throw error;
        saved = updated || payload;
      } else {
        const insertPayload = { ...payload };
        if (user.role !== 'client' && !insertPayload.created_by) {
          insertPayload.created_by = user.id;
        }

        const { data: inserted, error } = await supabase
          .from('financial_analyses_pf')
          .insert(insertPayload)
          .select()
          .maybeSingle();

        if (error) throw error;
        saved = inserted || payload;
      }

      setAnalysis(saved);
      return saved;
    } catch (err) {
      const isAuthError = err instanceof AuthApiError || err?.status === 401;
      const handled = isAuthError ? await handleAuthFailure(err, signOut) : false;
      let message = err.message;
      if (!handled) {
        console.error('Error saving analysis:', err);
        if (err?.details) {
          console.error('Supabase error details:', err.details);
        }
        console.error('Payload that triggered error:', payload);
        if (import.meta.env.DEV) {
          try {
            const { data: columns } = await supabase
              .from('information_schema.columns')
              .select('column_name, data_type, is_nullable')
              .eq('table_name', 'financial_analyses_pf');
            if (columns) {
              logDev('financial_analyses_pf column metadata:', columns);
            }
          } catch (schemaErr) {
            console.error('Error fetching column metadata:', schemaErr);
          }
        }
        if (err.code === '23503') {
          message = 'foreign key violation';
        } else if (err.code === '23502') {
          message = 'missing required field';
        } else if (err?.status === 400) {
          message = 'Invalid analysis data supplied.';
        } else {
          message = 'Failed to save analysis. Using local data only.';
        }
        setError(message);
      }
      setAnalysis(data);
      return { success: false, error: message };
    } finally {
      setSaving(false);
    }
  }, [user, signOut]);

  const addFinancialAnalysis = useCallback(
    async (data) => {
      if (!data?.client_id) {
        const message = 'A client_id is required to create a financial analysis.';
        setError(message);
        return { success: false, error: message };
      }

      if (!isValidUuid(data?.client_id) || !isValidUuid(user?.id)) {
        const message = 'Invalid client or user identifier.';
        setError(message);
        return { success: false, error: message };
      }

      let verifiedClientId;
      try {
        const [
          { data: clientRecord, error: clientError },
          { data: userRecord, error: userError }
        ] = await Promise.all([
          supabase
            .from('clients_pf')
            .select('id')
            .eq('id', data.client_id)
            .maybeSingle(),
          supabase
            .from('users_pf')
            .select('id')
            .eq('id', user.id)
            .maybeSingle()
        ]);

        if (clientError) throw clientError;
        if (userError) throw userError;

        if (!clientRecord) {
          const message =
            'Client record not found. Unable to create financial analysis.';
          setError(message);
          return { success: false, error: message };
        }

        verifiedClientId = clientRecord.id;

        if (!userRecord) {
          const message =
            'User record not found. Unable to create financial analysis.';
          setError(message);
          return { success: false, error: message };
        }
      } catch (err) {
        const isAuthError = err instanceof AuthApiError || err?.status === 401;
        const handled = isAuthError ? await handleAuthFailure(err, signOut) : false;
        const message =
          err?.message || 'Failed to verify client or user before creating analysis.';
        if (!handled) {
          console.error('Error verifying records before adding analysis:', err);
          setError(message);
        }
        return { success: false, error: message };
      }

      if (!verifiedClientId) {
        const message = 'Unable to verify client before creating analysis.';
        setError(message);
        return { success: false, error: message };
      }

      const cleanedInput = { ...data, client_id: verifiedClientId };
      const cleaned = applyDefaultAnalysisValues(cleanedInput);
      if (cleaned.id && cleaned.id.startsWith('new-analysis')) {
        delete cleaned.id;
      }

      const payload = sanitizeNumericFields({
        ...cleaned,
        client_id: verifiedClientId,
        created_by: user.id,
        fna_code: cleaned.fna_code || crypto.randomUUID()
      });
      logDev('Sanitized analysis payload before save:', payload);
      const result = await saveAnalysis(payload);
      if (result?.success === false) return result;
      return { success: true, data: result };
    },
    [user, saveAnalysis, supabase, signOut]
  );

  const updateFinancialAnalysis = useCallback(
    async (data) => {
      if (!isValidUuid(data?.id) || !isValidUuid(data?.client_id)) {
        const message = 'Invalid financial analysis identifier.';
        setError(message);
        return { success: false, error: message };
      }

      const payload = sanitizeNumericFields({ ...data });
      logDev('Sanitized analysis payload before save:', payload);
      const result = await saveAnalysis(payload);
      if (result?.success === false) return result;
      return { success: true, data: result };
    },
    [saveAnalysis]
  );

  // Update income sources only
  const saveIncomeSources = useCallback(async (analysisId, sources) => {
    try {
      setSaving(true);

      let updateQuery = supabase
        .from('financial_analyses_pf')
        .update({
          income_sources: sources,
          updated_at: getIsoTimestamp()
        })
        .eq('id', analysisId);

      if (user.role === 'client') {
        updateQuery = updateQuery.eq('client_id', user.id);
      } else if (user.role === 'advisor') {
        updateQuery = updateQuery.eq('created_by', user.id);
      } else if (user.role === 'admin') {
        // Admins do not require additional filters when updating income sources.
      }

      const { data, error } = await updateQuery
        .select()
        .maybeSingle();

      if (error) throw error;

      setAnalysis((prev) => (prev ? { ...prev, income_sources: sources } : prev));
      return { success: true, data };
    } catch (err) {
      const isAuthError = err instanceof AuthApiError || err?.status === 401;
      const handled = isAuthError ? await handleAuthFailure(err, signOut) : false;
      if (!handled) {
        console.error('Error saving income sources:', err);
        setError('Failed to save income sources. Using local data only.');
      }
      setAnalysis((prev) => (prev ? { ...prev, income_sources: sources } : prev));
      return { success: false, error: err.message };
    } finally {
      setSaving(false);
    }
  }, [user, signOut]);

  // Update expenses only
  const saveExpenses = useCallback(async (analysisId, expenses) => {
    try {
      setSaving(true);

      let updateQuery = supabase
        .from('financial_analyses_pf')
        .update({
          expenses: expenses,
          updated_at: getIsoTimestamp()
        })
        .eq('id', analysisId);

      if (user.role === 'client') {
        updateQuery = updateQuery.eq('client_id', user.id);
      } else if (user.role === 'advisor') {
        updateQuery = updateQuery.eq('created_by', user.id);
      } else if (user.role === 'admin') {
        // Admins do not require additional filters when updating expenses.
      }

      const { data, error } = await updateQuery
        .select()
        .maybeSingle();

      if (error) throw error;

      setAnalysis((prev) => (prev ? { ...prev, expenses: expenses } : prev));
      return { success: true, data };
    } catch (err) {
      const isAuthError = err instanceof AuthApiError || err?.status === 401;
      const handled = isAuthError ? await handleAuthFailure(err, signOut) : false;
      if (!handled) {
        console.error('Error saving expenses:', err);
        setError('Failed to save expenses. Using local data only.');
      }
      setAnalysis((prev) => (prev ? { ...prev, expenses: expenses } : prev));
      return { success: false, error: err.message };
    } finally {
      setSaving(false);
    }
  }, [user, signOut]);

  const value = {
    analysis,
    loading,
    saving,
    error,
    loadAnalysis,
    saveAnalysis,
    addFinancialAnalysis,
    updateFinancialAnalysis,
    saveIncomeSources,
    saveExpenses,
    setAnalysis,
  };

  return (
    <FinancialAnalysisContext.Provider value={value}>
      {children}
    </FinancialAnalysisContext.Provider>
  );
};

// Add default export
export default FinancialAnalysisProvider;
